import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useStudioStore } from '@/store/useStudioStore';
import { useCreatorStore } from '@/store/useCreatorStore';
import {
  stripLegacyLandingPageTrackingScript,
  stripLegacyGoogleAdsTrackingScript,
  removeLegacyLandingPageTrackingScriptsFromDocument,
  removeLegacyGoogleAdsTrackingScriptsFromDocument,
} from '@/lib/domUtils';
import {
  removeStudioGoogleAdsTrackingScriptsFromDocument,
  STUDIO_PREVIEW_WINDOW_NAME,
  stripStudioGoogleAdsTrackingScript,
} from '@/lib/studioGoogleAds';
import {
  removeStudioAnalyticsTrackingScriptsFromDocument,
  stripStudioAnalyticsTrackingScript,
} from '@/lib/studioAnalytics';
// @ts-expect-error generated JS
import { createLinkerHost, MessageType } from '@/lib/linker-parent.es.femf3n5t.js';
import {
  applyManualSelectionMarker,
  asSelectionElement,
  buildLabelSelectionFromIncomingPayload,
  buildPointerSelectionHintFromTarget,
  buildPreferredDirectLabelSelection,
  buildSelectedElementInfo,
  buildSelectionPromptInput,
  clearManualSelectionMarker,
  getActiveDirectSelectionLock,
  resolveManualMediaHighlight,
  type DirectSelectionLock,
  type LinkerSelectedPayload,
  type PointerSelectionHint,
} from './StudioPreview';

const DIRECT_SELECTION_LOCK_MS = 1500;

const injectLinkInterceptor = (html: string): string => {
  const script = `<script>(function(){
  document.addEventListener('click', function(e){
    var a = e.target.closest('a');
    if(!a) return;
    var href = a.getAttribute('href');
    if(!href) return;
    if(href.startsWith('#')){
      e.preventDefault();
      var el = document.getElementById(href.slice(1));
      if(el) el.scrollIntoView({behavior:'smooth'});
      return;
    }
    try {
      var url = new URL(href, location.href);
      if(url.origin !== location.origin){ e.preventDefault(); }
    } catch(_){ e.preventDefault(); }
  }, true);
})();</script>`;
  if (html.includes('</body>')) return html.replace('</body>', script + '</body>');
  if (html.includes('</html>')) return html.replace('</html>', script + '</html>');
  return html + script;
};

const getHtmlContentKey = (html: string) => {
  let hash = 0;
  for (let index = 0; index < html.length; index += 1) {
    hash = (hash * 31 + html.charCodeAt(index)) >>> 0;
  }
  return `gads-selection-srcdoc-${html.length}-${hash}`;
};

const getDocumentWidth = (doc: Document | null | undefined) =>
  Math.max(
    doc?.documentElement?.scrollWidth || 0,
    doc?.documentElement?.clientWidth || 0,
    doc?.body?.scrollWidth || 0,
    doc?.body?.clientWidth || 0,
  );

const getDocumentHeight = (doc: Document | null | undefined) =>
  Math.max(
    doc?.documentElement?.scrollHeight || 0,
    doc?.documentElement?.clientHeight || 0,
    doc?.body?.scrollHeight || 0,
    doc?.body?.clientHeight || 0,
  );

interface GoogleAdsSelectionPreviewProps {
  isActive: boolean;
}

export const GoogleAdsSelectionPreview = ({ isActive }: GoogleAdsSelectionPreviewProps) => {
  const iframeUrl = useStudioStore((state) => state.iframeUrl);
  const generatedHtml = useStudioStore((state) => state.generatedHtml);
  const previewHtmlOverride = useStudioStore((state) => state.previewHtmlOverride);
  const previewReview = useStudioStore((state) => state.previewReview);
  const sourcePreviewFrame = useStudioStore((state) => state.iframeElement);
  const { selectedElement, setPromptInput, setSelectedElement } = useCreatorStore();

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastPointerSelectionHintRef = useRef<PointerSelectionHint | null>(null);
  const directSelectionLockRef = useRef<DirectSelectionLock | null>(null);
  const [loadedPreviewContentKey, setLoadedPreviewContentKey] = useState<string | null>(null);
  const [iframeLoadVersion, setIframeLoadVersion] = useState(0);
  const [frameMetrics, setFrameMetrics] = useState({
    width: 980,
    height: 1200,
    scale: 1,
  });

  const displayedGeneratedHtml = useMemo(() => {
    if (!previewReview.active) {
      return previewHtmlOverride || generatedHtml;
    }

    return previewReview.view === 'before' ? previewReview.beforeHtml : previewReview.afterHtml;
  }, [generatedHtml, previewHtmlOverride, previewReview]);

  const processedHtml = useMemo(
    () =>
      displayedGeneratedHtml
        ? injectLinkInterceptor(
            stripLegacyLandingPageTrackingScript(
              stripLegacyGoogleAdsTrackingScript(
                stripStudioAnalyticsTrackingScript(
                  stripStudioGoogleAdsTrackingScript(displayedGeneratedHtml),
                ),
              ),
            ),
          )
        : null,
    [displayedGeneratedHtml],
  );

  const previewContentKey = useMemo(
    () => (processedHtml ? getHtmlContentKey(processedHtml) : iframeUrl || 'empty'),
    [iframeUrl, processedHtml],
  );

  const isIframeLoaded = loadedPreviewContentKey === previewContentKey;
  const hasPreviewSource = Boolean(processedHtml || iframeUrl);

  const measureScaledFrame = useCallback(() => {
    const viewport = viewportRef.current;
    const iframe = iframeRef.current;
    if (!viewport || !iframe) return;

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    const sourceDoc =
      sourcePreviewFrame?.contentDocument || sourcePreviewFrame?.contentWindow?.document || null;
    const nextWidth =
      getDocumentWidth(sourceDoc) ||
      getDocumentWidth(iframeDoc) ||
      sourcePreviewFrame?.clientWidth ||
      980;
    const nextHeight =
      getDocumentHeight(sourceDoc) ||
      getDocumentHeight(iframeDoc) ||
      sourcePreviewFrame?.clientHeight ||
      1200;
    const nextScale = Math.min(1, Math.max(0.25, (viewport.clientWidth - 18) / nextWidth));

    setFrameMetrics({
      width: Math.max(480, nextWidth),
      height: Math.max(640, nextHeight),
      scale: nextScale,
    });
  }, [sourcePreviewFrame]);

  const applySelectedInfo = useCallback(
    (selectedInfo: ReturnType<typeof buildSelectedElementInfo>, fallbackText?: string) => {
      setSelectedElement(selectedInfo);
      setPromptInput(buildSelectionPromptInput(selectedInfo, fallbackText));
    },
    [setPromptInput, setSelectedElement],
  );

  useEffect(() => {
    if (!isActive) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    const resizeObserver = new ResizeObserver(() => {
      measureScaledFrame();
    });
    resizeObserver.observe(viewport);

    window.addEventListener('resize', measureScaledFrame);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureScaledFrame);
    };
  }, [isActive, measureScaledFrame]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const iframeWindow = iframe?.contentWindow;
    const iframeDoc = iframe?.contentDocument || iframeWindow?.document;
    if (!isActive || !iframeWindow || !iframeDoc || !isIframeLoaded) {
      lastPointerSelectionHintRef.current = null;
      directSelectionLockRef.current = null;
      return;
    }

    const rememberPointerTarget = (event: Event) => {
      const supportsPointerEvents = 'onpointerdown' in iframeWindow;
      const shouldResetDirectSelectionLock =
        event.type === 'pointerdown' || (event.type === 'mousedown' && !supportsPointerEvents);

      if (shouldResetDirectSelectionLock) {
        directSelectionLockRef.current = null;
      }

      const pointerEvent = event as MouseEvent;
      lastPointerSelectionHintRef.current = {
        target: asSelectionElement(event.target),
        clientX: pointerEvent.clientX,
        clientY: pointerEvent.clientY,
      };
    };

    iframeWindow.addEventListener('pointerdown', rememberPointerTarget, true);
    iframeWindow.addEventListener('mousedown', rememberPointerTarget, true);

    return () => {
      iframeWindow.removeEventListener('pointerdown', rememberPointerTarget, true);
      iframeWindow.removeEventListener('mousedown', rememberPointerTarget, true);
      lastPointerSelectionHintRef.current = null;
    };
  }, [iframeLoadVersion, isActive, isIframeLoaded, previewContentKey]);

  useEffect(() => {
    if (selectedElement) return;

    const iframeDoc =
      iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    if (iframeDoc) {
      clearManualSelectionMarker(iframeDoc);
    }
  }, [selectedElement]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const currentUrl = processedHtml ? 'srcdoc' : iframeUrl || null;
    if (!isActive || !currentUrl || !iframe || !isIframeLoaded) {
      return;
    }

    const host = createLinkerHost(iframe);
    host.on(MessageType.ELEMENT_SELECTED, (data: LinkerSelectedPayload) => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      const preferredHint = lastPointerSelectionHintRef.current;
      const incomingLabelSelection = buildLabelSelectionFromIncomingPayload(
        iframeDoc,
        data,
        preferredHint,
      );
      if (incomingLabelSelection) {
        directSelectionLockRef.current = {
          ...incomingLabelSelection,
          expiresAt: Date.now() + DIRECT_SELECTION_LOCK_MS,
        };
      }

      const activeDirectSelectionLock = getActiveDirectSelectionLock(
        directSelectionLockRef.current,
      );
      if (!activeDirectSelectionLock && directSelectionLockRef.current) {
        directSelectionLockRef.current = null;
      }

      const lockedLabelSelection = activeDirectSelectionLock?.target.matches('label')
        ? {
            target: activeDirectSelectionLock.target,
            selectionRoot: activeDirectSelectionLock.selectionRoot,
            payload: activeDirectSelectionLock.payload,
          }
        : null;
      const preferredLabelSelection =
        lockedLabelSelection ||
        incomingLabelSelection ||
        buildPreferredDirectLabelSelection(preferredHint);
      const shouldForceLockedLabelSelection = Boolean(lockedLabelSelection);
      const effectiveData =
        shouldForceLockedLabelSelection && lockedLabelSelection
          ? lockedLabelSelection.payload
          : preferredLabelSelection
            ? preferredLabelSelection.payload
            : data;
      const effectiveHint: PointerSelectionHint | null =
        shouldForceLockedLabelSelection && lockedLabelSelection
          ? buildPointerSelectionHintFromTarget(lockedLabelSelection.target, preferredHint)
          : preferredLabelSelection
            ? buildPointerSelectionHintFromTarget(preferredLabelSelection.target, preferredHint)
            : preferredHint;

      if (iframeDoc) {
        clearManualSelectionMarker(iframeDoc);
      }

      const selectedInfo = buildSelectedElementInfo(iframe, effectiveData, effectiveHint);
      lastPointerSelectionHintRef.current = null;
      applySelectedInfo(selectedInfo, effectiveData.text);

      if (!iframeDoc) return;

      if ((shouldForceLockedLabelSelection && lockedLabelSelection) || preferredLabelSelection) {
        const labelSelection = lockedLabelSelection || preferredLabelSelection;
        if (!labelSelection) return;

        applyManualSelectionMarker(
          iframeDoc,
          labelSelection.target,
          labelSelection.selectionRoot === labelSelection.target
            ? null
            : labelSelection.selectionRoot,
        );
        return;
      }

      const manualMediaHighlight = resolveManualMediaHighlight(
        iframe,
        effectiveData,
        selectedInfo,
        effectiveHint,
      );

      if (manualMediaHighlight) {
        applyManualSelectionMarker(
          iframeDoc,
          manualMediaHighlight.target,
          manualMediaHighlight.suppressedElement,
        );
      }
    });

    host.on(MessageType.PING, () => {
      host.send(MessageType.ACTIVATE, {});
    });

    host.send(MessageType.ACTIVATE, {});

    return () => {
      host.send?.(MessageType.DEACTIVATE, {});
      host.destroy?.();
    };
  }, [
    applySelectedInfo,
    iframeLoadVersion,
    iframeUrl,
    isActive,
    isIframeLoaded,
    previewContentKey,
    processedHtml,
  ]);

  const handlePreviewLoad = useCallback(() => {
    const iframeDoc =
      iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    try {
      removeLegacyLandingPageTrackingScriptsFromDocument(iframeDoc);
      removeLegacyGoogleAdsTrackingScriptsFromDocument(iframeDoc);
      removeStudioGoogleAdsTrackingScriptsFromDocument(iframeDoc);
      removeStudioAnalyticsTrackingScriptsFromDocument(iframeDoc);
    } catch {
      // Ignore iframe access failures.
    }

    setLoadedPreviewContentKey(previewContentKey);
    setIframeLoadVersion((version) => version + 1);
    window.requestAnimationFrame(() => {
      measureScaledFrame();
      window.setTimeout(measureScaledFrame, 60);
    });
  }, [measureScaledFrame, previewContentKey]);

  if (!hasPreviewSource) {
    return (
      <div className="flex h-full items-center justify-center bg-[#E5E7EB] px-6 text-center text-[12.5px] text-[#0D0D1A]/45">
        Preview is not ready yet.
      </div>
    );
  }

  const scaledWidth = Math.ceil(frameMetrics.width * frameMetrics.scale);
  const scaledHeight = Math.ceil(frameMetrics.height * frameMetrics.scale);

  return (
    <div ref={viewportRef} className="h-full overflow-auto bg-[#E5E7EB] p-[9px]">
      <div
        className="relative mx-auto"
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
        }}
      >
        {processedHtml ? (
          <iframe
            key={previewContentKey}
            ref={iframeRef}
            name={STUDIO_PREVIEW_WINDOW_NAME}
            srcDoc={processedHtml}
            onLoad={handlePreviewLoad}
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
            className={cn(
              'absolute top-0 left-0 border-none bg-white transition-opacity duration-200',
              !isIframeLoaded && 'opacity-0',
            )}
            style={{
              width: `${frameMetrics.width}px`,
              height: `${frameMetrics.height}px`,
              transform: `scale(${frameMetrics.scale})`,
              transformOrigin: 'top left',
            }}
            title="Google Ads Selection Preview"
          />
        ) : (
          <iframe
            key={previewContentKey}
            ref={iframeRef}
            name={STUDIO_PREVIEW_WINDOW_NAME}
            src={iframeUrl || undefined}
            onLoad={handlePreviewLoad}
            className={cn(
              'absolute top-0 left-0 border-none bg-white transition-opacity duration-200',
              !isIframeLoaded && 'opacity-0',
            )}
            style={{
              width: `${frameMetrics.width}px`,
              height: `${frameMetrics.height}px`,
              transform: `scale(${frameMetrics.scale})`,
              transformOrigin: 'top left',
            }}
            title="Google Ads Selection Preview"
          />
        )}
      </div>
    </div>
  );
};
